import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { Calendar, X } from 'lucide-react';
import { subDays, format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';

interface DateRange {
  start: string; // ISO format
  end: string;   // ISO format
}

interface PresetOption {
  label: string;
  value: string;
  days: number;
  visible: boolean;
}

interface DateRangePickerProps {
  value?: DateRange | null;
  onChange: (range: DateRange | null, period: string) => void;
  maxRetentionDays: number;
  disabled?: boolean;
  selectedPeriod?: string; // Allow parent to control selected period
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  maxRetentionDays,
  disabled = false,
  selectedPeriod
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string>(selectedPeriod || 'month');

  // Sync internal state when selectedPeriod prop changes
  useEffect(() => {
    if (selectedPeriod && selectedPeriod !== 'custom') {
      setSelectedPreset(selectedPeriod);

      // Also update the dates to match the preset
      const preset = allPresets.find(p => p.value === selectedPeriod);
      if (preset) {
        const end = new Date();
        const start = subDays(end, preset.days - 1);
        setStartDate(start);
        setEndDate(end);
      }
    } else if (selectedPeriod === 'custom') {
      setSelectedPreset('custom');
    }
  }, [selectedPeriod]);

  // Initialize dates from value
  useEffect(() => {
    if (value) {
      // Parse date strings as local dates (not UTC)
      const parseLocalDate = (dateStr: string): Date => {
        const [year, month, day] = dateStr.split('-').map(Number);
        return new Date(year, month - 1, day);
      };

      setStartDate(parseLocalDate(value.start));
      setEndDate(parseLocalDate(value.end));
    }
  }, [value]);

  const today = new Date();
  const minDate = subDays(today, maxRetentionDays);

  // Define all preset options
  const allPresets: PresetOption[] = [
    { label: 'Today', value: 'day', days: 1, visible: true },
    { label: 'Last 7 Days', value: 'week', days: 7, visible: maxRetentionDays >= 7 },
    { label: 'Last 30 Days', value: 'month', days: 30, visible: maxRetentionDays >= 30 },
    { label: 'Last 60 Days', value: '60days', days: 60, visible: maxRetentionDays >= 60 },
    { label: 'Last 90 Days', value: '90days', days: 90, visible: maxRetentionDays >= 90 }
  ];

  const visiblePresets = allPresets.filter(p => p.visible);

  // Format dates to local date string without timezone conversion
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handlePresetClick = (preset: PresetOption) => {
    const end = new Date();
    const start = subDays(end, preset.days - 1);

    setStartDate(start);
    setEndDate(end);
    setSelectedPreset(preset.value);

    onChange(
      {
        start: formatLocalDate(start),
        end: formatLocalDate(end)
      },
      preset.value
    );

    setIsOpen(false);
  };

  const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);

    if (start && end) {
      setSelectedPreset('custom');

      const formattedStart = formatLocalDate(start);
      const formattedEnd = formatLocalDate(end);

      onChange(
        {
          start: formattedStart,
          end: formattedEnd
        },
        'custom'
      );

      // Close the picker after both dates are selected (consistent with preset behavior)
      setIsOpen(false);
    }
  };


  const getDisplayText = () => {
    if (!startDate || !endDate) {
      const defaultPreset = visiblePresets.find(p => p.value === 'month') || visiblePresets[0];
      return defaultPreset.label;
    }

    if (selectedPreset === 'custom') {
      return `${format(startDate, 'MMM d, yyyy')} - ${format(endDate, 'MMM d, yyyy')}`;
    }

    const preset = visiblePresets.find(p => p.value === selectedPreset);
    return preset ? preset.label : 'Custom Range';
  };

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors shadow-sm"
      >
        <Calendar className="h-3.5 w-3.5 mr-1.5" />
        {getDisplayText()}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Picker Popover */}
          <div className="absolute top-full right-0 mt-2 z-50 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
            <div className="flex">
              {/* Left side - Presets */}
              <div className="w-32 bg-gray-50 border-r border-gray-200 p-2">
                <div className="space-y-0.5">
                  {visiblePresets.map((preset) => (
                    <button
                      key={preset.value}
                      onClick={() => handlePresetClick(preset)}
                      className={`w-full text-left px-2 py-1.5 text-xs rounded-md transition-colors ${
                        selectedPreset === preset.value
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}

                  <div className="pt-1.5 border-t border-gray-300 mt-1.5">
                    <button
                      onClick={() => {
                        setSelectedPreset('custom');
                        setStartDate(null);
                        setEndDate(null);
                      }}
                      className={`w-full text-left px-2 py-1.5 text-xs rounded-md transition-colors ${
                        selectedPreset === 'custom'
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Custom Range
                    </button>
                  </div>
                </div>
              </div>

              {/* Right side - Calendar */}
              <div className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-900">
                    {selectedPreset === 'custom' ? 'Select Date Range' : 'Preview'}
                  </h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-0.5 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <X className="h-3.5 w-3.5 text-gray-500" />
                  </button>
                </div>

                {selectedPreset === 'custom' ? (
                  <div className="date-picker-small">
                    <DatePicker
                      selected={startDate}
                      onChange={handleCustomDateChange}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      inline
                      minDate={minDate}
                      maxDate={today}
                      monthsShown={2}
                    />
                    <style>{`
                      .date-picker-small .react-datepicker {
                        font-size: 0.75rem;
                        border: none;
                      }
                      .date-picker-small .react-datepicker__header {
                        background-color: white;
                        border-bottom: 1px solid #e5e7eb;
                        padding-top: 0.5rem;
                      }
                      .date-picker-small .react-datepicker__current-month {
                        font-size: 0.75rem;
                        font-weight: 600;
                        color: #374151;
                        margin-bottom: 0.25rem;
                      }
                      .date-picker-small .react-datepicker__day-name {
                        font-size: 0.625rem;
                        color: #6b7280;
                        width: 1.75rem;
                        line-height: 1.75rem;
                        margin: 0.1rem;
                      }
                      .date-picker-small .react-datepicker__day {
                        font-size: 0.7rem;
                        width: 1.75rem;
                        line-height: 1.75rem;
                        margin: 0.1rem;
                        border-radius: 0.25rem;
                      }
                      .date-picker-small .react-datepicker__day:hover {
                        background-color: #f3f4f6;
                      }
                      .date-picker-small .react-datepicker__day--selected,
                      .date-picker-small .react-datepicker__day--in-range {
                        background-color: #3b82f6;
                        color: white;
                      }
                      .date-picker-small .react-datepicker__day--in-selecting-range {
                        background-color: #93c5fd;
                      }
                      .date-picker-small .react-datepicker__day--keyboard-selected {
                        background-color: #dbeafe;
                      }
                      .date-picker-small .react-datepicker__navigation {
                        top: 0.5rem;
                      }
                      .date-picker-small .react-datepicker__navigation-icon::before {
                        border-width: 2px 2px 0 0;
                        height: 6px;
                        width: 6px;
                      }
                      .date-picker-small .react-datepicker__month-container {
                        float: left;
                      }
                      .date-picker-small .react-datepicker__day--outside-month {
                        color: #d1d5db;
                      }
                    `}</style>
                  </div>
                ) : (
                  <div className="text-center py-6 px-10">
                    <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-xs text-gray-600">
                      Click "Custom Range" to select specific dates
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Or select a preset from the left
                    </p>
                  </div>
                )}

              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
